import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AIRPORTS as MAJOR_AIRPORTS, type Airport } from '../data/airports';

type OriginValue = {
    city: string;
    airport: string;
    source: 'manual' | 'browser' | 'ip';
    _v?: number;
};

type OriginContextValue = {
    origin: OriginValue | null;
    setOrigin: (o: Omit<OriginValue, '_v'> | OriginValue | null) => void;
    loading: boolean;
};

type OriginProviderProps = {
    children: React.ReactNode;
};

const OriginContext = createContext<OriginContextValue | null>(null);

const STORAGE_KEY = 'muno_origin';
const STORAGE_VERSION = 2; // bump to force re-detection for existing users

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function nearestAirport(lat: number, lon: number): Airport {
    let best = MAJOR_AIRPORTS[0];
    let bestDist = Infinity;
    for (const ap of MAJOR_AIRPORTS) {
        const d = haversineKm(lat, lon, ap.lat, ap.lon);
        if (d < bestDist) { bestDist = d; best = ap; }
    }
    return best;
}

export const OriginProvider = ({ children }: OriginProviderProps) => {
    const [origin, setOriginState] = useState<OriginValue | null>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Only trust stored value if it has the current version AND was manually set
                if (parsed._v === STORAGE_VERSION && parsed.source === 'manual') return parsed;
            }
        } catch { /* ignore */ }
        return null;
    });
    const [loading, setLoading] = useState(true);

    const setOrigin = useCallback((o: Omit<OriginValue, '_v'> | OriginValue | null) => {
        const val = o ? { ...o, _v: STORAGE_VERSION } : null;
        setOriginState(val);
        if (val) localStorage.setItem(STORAGE_KEY, JSON.stringify(val));
        else localStorage.removeItem(STORAGE_KEY);
    }, []);

    /* Find airport by city name (case-insensitive) */
    const airportByCity = (cityName: string) => {
        if (!cityName) return null;
        const lower = cityName.toLowerCase();
        return MAJOR_AIRPORTS.find(ap => ap.city.toLowerCase() === lower) || null;
    };

    /* Auto-detect on every load unless user manually set origin */
    useEffect(() => {
        if (origin?.source === 'manual') { setLoading(false); return; }

        let cancelled = false;

        async function detect() {
            // 1) Try browser geolocation
            if (navigator.geolocation) {
                try {
                    const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 6000, maximumAge: 300000 });
                    });
                    if (cancelled) return;
                    const ap = nearestAirport(pos.coords.latitude, pos.coords.longitude);
                    setOrigin({ city: ap.city, airport: ap.code, source: 'browser' });
                    setLoading(false);
                    return;
                } catch { /* permission denied or timeout — fall through */ }
            }

            // 2) IP-based lookup (primary: ipapi.co)
            if (cancelled) return;
            try {
                const controller = new AbortController();
                const timer = setTimeout(() => controller.abort(), 4000);
                const resp = await fetch('https://ipapi.co/json/', { signal: controller.signal });
                clearTimeout(timer);
                if (cancelled) return;
                if (resp.ok) {
                    const data = await resp.json();
                    if (data.latitude && data.longitude) {
                        const ap = nearestAirport(data.latitude, data.longitude);
                        setOrigin({ city: ap.city, airport: ap.code, source: 'ip' });
                        setLoading(false);
                        return;
                    }
                    // If no lat/lon but city is available, try matching by name
                    if (data.city) {
                        const ap = airportByCity(data.city);
                        if (ap) {
                            setOrigin({ city: ap.city, airport: ap.code, source: 'ip' });
                            setLoading(false);
                            return;
                        }
                    }
                }
            } catch { /* network error — fall through */ }

            // 3) IP-based lookup (backup: ip-api.com)
            if (cancelled) return;
            try {
                const controller = new AbortController();
                const timer = setTimeout(() => controller.abort(), 4000);
                const resp = await fetch('http://ip-api.com/json/?fields=lat,lon,city', { signal: controller.signal });
                clearTimeout(timer);
                if (cancelled) return;
                if (resp.ok) {
                    const data = await resp.json();
                    if (data.lat && data.lon) {
                        const ap = nearestAirport(data.lat, data.lon);
                        setOrigin({ city: ap.city, airport: ap.code, source: 'ip' });
                        setLoading(false);
                        return;
                    }
                    if (data.city) {
                        const ap = airportByCity(data.city);
                        if (ap) {
                            setOrigin({ city: ap.city, airport: ap.code, source: 'ip' });
                            setLoading(false);
                            return;
                        }
                    }
                }
            } catch { /* fall through */ }

            // 4) Final fallback
            if (!cancelled) {
                setOrigin({ city: 'New York', airport: 'JFK', source: 'ip' });
                setLoading(false);
            }
        }

        detect();
        return () => { cancelled = true; };
    }, []);

    const value = { origin, setOrigin, loading };

    return <OriginContext.Provider value={value}>{children}</OriginContext.Provider>;
};

export const useOrigin = () => {
    const ctx = useContext(OriginContext);
    if (!ctx) throw new Error('useOrigin must be used within OriginProvider');
    return ctx;
};


